import { isPlaceholderStock } from "./placeholderStock";

// Where a listing's Base Price comes from.
//
// ERP-5169 (ECS): a listing mapped to WooCommerce does not have a Base Price of
// its own — the backend keeps it equal to the WooCommerce platform price on every
// write path (`sync_listing_base_price_from_woo`, sales/utils.py), including the
// hourly `scan_woocommerce_products` cron and list/delist. Letting the user type a
// Base Price on such a listing is therefore a lie: the value is overwritten by the
// next sync, silently. Those inputs are disabled instead.
//
// The rule has to match the backend's exactly, or the screen disables a field the
// server would have honoured (or the reverse). `woo_base_price_for_listing` takes
// the first WooCommerce mapping, by sort_order, whose platform price is > 0 —
// **listed or delisted**, and **whatever its sync status** — and returns None when
// no Woo mapping has a price. None means "no Woo price yet", and then the manual
// Base Price stands and stays editable. So the question here is only: does any
// WooCommerce mapping on this listing carry a price above zero?
//
// Note this reads `platformPriceRaw`, not the displayed `platformPrice`: the grid
// blanks the display to "-" on a failed sync, but the stored price still drives the
// base price, exactly as it does server-side. When there is no platform price at all
// — the normal state of a DELISTED Woo mapping — the ERP override stands in, again
// matching the server.

export const isWooPlatformName = (name) => /woocommerce/i.test(String(name || ""));

// Takes a UI listing ({ name: spu_name, variant: sku_name, platforms }) — the shape
// both Platform Pricing and Bulk Pricing carry — because the backend's rule has one
// more clause than the Woo-price test: `sync_listing_base_price_from_woo` returns
// early for the "No Connected Model" placeholder listing, so its base price is
// manual no matter what its Woo mappings are priced at. Reading only `platforms`
// would lock the Base Price of every placeholder row the server was still happy to
// let anyone edit.
// Strictly greater than zero, and 0 is the case that matters: a WooCommerce mapping
// priced at **0** is not "priced at nothing", it is a mapping with no usable price,
// and the listing has to behave exactly as if WooCommerce were not mapped at all —
// Base Price editable, breathing dot when it is empty, no "will match WooCommerce"
// caption anywhere. `null` and `0` therefore have to give the same answer here, and
// the backend's `woo_base_price_for_listing` applies the same `> 0` test on both of
// its sources so the two can never disagree.
const positive = (v) => v != null && Number(v) > 0;

export const wooBasePriceDriver = (listing) => {
    if (!listing) return null;
    if (isPlaceholderStock(listing.name, listing.variant)) return null;
    // `allPlatforms` over `platforms` (Jess, 16 Sep): Bulk Pricing is handed only the
    // mappings the user ticked, so a stock mapped to Shopee + TikTok + Lazada +
    // WooCommerce with just Shopee selected carried no Woo mapping at all, and its
    // Base Price unlocked — while the server still re-derived it from Woo on save.
    // Platform Pricing now passes the full list alongside the selection.
    const found = (listing.allPlatforms || listing.platforms || []).find((p) => {
        if (!isWooPlatformName(p && p.name)) return false;
        // Same two sources, in the same order, as woo_base_price_for_listing:
        // what WooCommerce itself is charging, and failing that the ERP override.
        // The fallback is what makes "listed or delisted" true — delisting pushes an
        // EMPTY price to WooCommerce, so a delisted mapping's platform price is NULL
        // and the override is the only figure there is. Without it every delisted Woo
        // listing looked unmapped to this screen.
        return positive(p.platformPriceRaw) || positive(p.override_price_raw);
    });
    if (found) return found;
    // The mapping list itself can be incomplete: initiate-pricing-v2 narrows
    // `platforms` to the mappings matching the page's platform / status filter
    // (ERP-4979), so with the Platform Pricing filter set to Shopee the Woo mapping
    // never reaches the browser. The serializer's `woo_base_price` is judged over
    // every mapping, so it is the authority when the scan above finds nothing. The
    // stand-in carries no mapping id — Bulk Pricing then shows the stored Base Price
    // (which IS the Woo price) instead of mirroring a field that is not on screen.
    if (positive(listing.wooBasePrice)) {
        return { name: "WooCommerce", platformPriceRaw: Number(listing.wooBasePrice), hidden: true };
    }
    return null;
};

// `.find`, not `.filter`: the backend takes the FIRST such mapping by sort_order,
// and the API returns platforms in that order, so the first match here is the same
// mapping the server would pick. Bulk Pricing needs the mapping itself, not just a
// yes/no, so it can mirror the price the user is typing into that platform's field.
export const isWooDrivenBasePrice = (listing) => wooBasePriceDriver(listing) != null;

// A Base Price counts as "not filled in" at 0 as well as null — the grids already
// render 0 as "-", and 0 is not a price anyone meant to set.
export const isBasePriceEmpty = (value) => {
    if (value === null || value === undefined || value === "") return true;
    const num = Number(String(value).replace(/,/g, ""));
    return isNaN(num) || num <= 0;
};

export const WOO_BASE_PRICE_LOCKED_TITLE =
    "Base Price follows this listing's WooCommerce price and is kept in sync automatically — edit the price in WooCommerce instead.";

export const BASE_PRICE_BREATHE_TITLE =
    "Base Price is empty. Add one so Use Base Price and Pricing Template can work on this listing.";
